import { useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toPng } from "html-to-image";
import { toast } from "sonner";
import { Download, Share2, Home } from "lucide-react";
import { SubHeader } from "@/components/Brand";
import { useApi } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { rp, fmtAcc } from "@/lib/format";

const C = { navy: "#0f172a", muted: "#475569", blue: "#1d4ed8", red: "#dc2626", line: "#cbd5e1" };

const fmtWib = (s) => {
  const p = Object.fromEntries(new Intl.DateTimeFormat("id-ID", {
    timeZone: "Asia/Jakarta", day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false,
  }).formatToParts(new Date(s)).map((x) => [x.type, x.value]));
  return `${p.day} ${p.month.replace(".", "")} ${p.year}, ${p.hour}:${p.minute}:${p.second} WIB`;
};

const zigzag = {
  WebkitMask: "conic-gradient(from -45deg at bottom, #0000, #000 1deg 89deg, #0000 90deg) bottom/14px 51% repeat-x, conic-gradient(from 135deg at top, #0000, #000 1deg 89deg, #0000 90deg) top/14px 51% repeat-x",
  mask: "conic-gradient(from -45deg at bottom, #0000, #000 1deg 89deg, #0000 90deg) bottom/14px 51% repeat-x, conic-gradient(from 135deg at top, #0000, #000 1deg 89deg, #0000 90deg) top/14px 51% repeat-x",
};

const Row = ({ label, value, sub, big, testid }) => (
  <div className="flex justify-between items-start gap-4 py-3">
    <span style={{ color: C.muted }} className="text-[15px]">{label}</span>
    <div className="text-right">
      <div style={{ color: C.navy }} className={`font-bold tabular-nums ${big ? "text-xl" : "text-[17px] tracking-wide"}`} data-testid={testid}>{value}</div>
      {sub && <div style={{ color: C.navy }} className="text-sm mt-0.5">{sub}</div>}
    </div>
  </div>
);

export const TransferReceiptCard = ({ r, cardRef, incoming }) => (
  <div className="mx-auto w-full max-w-[380px]" style={{ filter: "drop-shadow(0 6px 14px rgba(15,23,42,0.14))" }}>
  <div ref={cardRef} className="bg-white keep-white w-full px-7 pt-9 pb-9" style={{ ...zigzag, backgroundColor: "#ffffff" }} data-testid="transfer-receipt">
    <div className="flex justify-center">
      <img src={`${process.env.PUBLIC_URL}/brand-banner.webp`} alt="Credit Union Tuah Talino - Koperasi Simpan Pinjam" className="h-[78px] w-auto object-contain" data-testid="receipt-logo" />
    </div>
    <div className="flex justify-center mt-7">
      <div className="h-[60px] w-[60px] rounded-full flex items-center justify-center" style={{ backgroundColor: "#22c55e" }}>
        <svg viewBox="0 0 24 24" className="h-8 w-8" fill="none" stroke="#fff" strokeWidth="3.2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12.5l4.5 4.5L19 7.5" /></svg>
      </div>
    </div>
    <h1 style={{ color: C.navy }} className="text-center text-[28px] font-extrabold mt-4 tracking-tight" data-testid="receipt-title">{incoming ? "Dana Diterima" : "Transfer Berhasil"}</h1>
    <p style={{ color: C.muted }} className="text-center text-[15px] mt-1" data-testid="receipt-ref">No. Ref: {r.ref_no || r.req_id.toUpperCase()}</p>
    <p style={{ color: C.muted }} className="text-center text-[15px] mt-1" data-testid="receipt-datetime">{fmtWib(r.decided_at || r.created_at)}</p>
    <div className="my-5 border-t-2 border-dashed" style={{ borderColor: C.line }} />
    <Row label="Dari Rekening" value={fmtAcc(r.from_account_no)} sub={r.member_name} testid="receipt-from" />
    <Row label="Ke Rekening" value={fmtAcc(r.target_account_no)} sub={r.recipient_name} testid="receipt-to" />
    <Row label="Nominal Transfer" value={rp(r.amount)} big testid="receipt-amount" />
    {!incoming && <Row label="Biaya Layanan Sistem" value={rp(r.fee || 0)} testid="receipt-fee" />}
    <div className="flex justify-between items-center rounded-md px-4 py-4 -mx-4 mt-1" style={{ backgroundColor: "#e0efff" }}>
      <span style={{ color: C.navy }} className="text-lg font-semibold">{incoming ? "Total Diterima" : "Total"}</span>
      <span style={{ color: C.navy }} className="text-xl font-extrabold tabular-nums" data-testid="receipt-total">{rp(r.amount + (incoming ? 0 : r.fee || 0))}</span>
    </div>
    <div className="my-6 border-t-2 border-dashed" style={{ borderColor: C.line }} />
    <p style={{ color: C.muted }} className="text-center text-[15px] leading-snug">Terima kasih telah menggunakan<br />layanan CU TUAH TALINO.</p>
  </div>
  </div>
);

export default function TransferReceipt() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { data: r, loading } = useApi(`/requests/${id}`);
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);

  const makeImage = () => toPng(ref.current, { pixelRatio: 3, backgroundColor: "transparent", cacheBust: true });
  const download = async () => {
    setBusy(true);
    try {
      const url = await makeImage();
      const a = document.createElement("a");
      a.href = url;
      a.download = `bukti-transfer-${r.ref_no || r.req_id}.png`;
      a.click();
    } catch {
      toast.error("Gagal membuat gambar bukti transfer");
    } finally {
      setBusy(false);
    }
  };
  const share = async () => {
    setBusy(true);
    try {
      const blob = await (await fetch(await makeImage())).blob();
      const file = new File([blob], `bukti-transfer-${r.ref_no || r.req_id}.png`, { type: "image/png" });
      if (navigator.canShare?.({ files: [file] })) await navigator.share({ files: [file], title: "Bukti Transfer CU Tuah Talino" });
      else toast.info("Perangkat tidak mendukung berbagi langsung, gunakan Unduh Bukti");
    } catch (e) {
      if (e?.name !== "AbortError") toast.error("Gagal membagikan bukti transfer");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="pb-8 min-h-full bg-slate-100 keep-bg">
      <SubHeader title="Bukti Transfer" />
      <div className="px-5 pt-2">
        {!r ? (
          <div className="text-center py-10" data-testid="receipt-state">
            <p className="text-sm text-slate-500">{loading ? "Memuat..." : "Bukti transfer tidak ditemukan."}</p>
            {!loading && <button onClick={() => navigate("/app/transaksi")} data-testid="receipt-back" className="mt-4 bg-blue-800 text-white font-semibold px-5 py-2.5 rounded-xl">Kembali ke Transaksi</button>}
          </div>
        ) : <TransferReceiptCard r={r} cardRef={ref} incoming={r.target_user_id === user?.user_id && r.user_id !== user?.user_id} />}
        {r && (
          <div className="grid grid-cols-2 gap-3 mt-6 max-w-[380px] mx-auto">
            <button onClick={download} disabled={busy} data-testid="receipt-download" className="flex items-center justify-center gap-2 bg-blue-800 hover:bg-blue-900 disabled:opacity-60 text-white font-semibold py-3 rounded-xl"><Download className="h-4 w-4" />Unduh Bukti</button>
            <button onClick={share} disabled={busy} data-testid="receipt-share" className="flex items-center justify-center gap-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white font-semibold py-3 rounded-xl"><Share2 className="h-4 w-4" />Bagikan</button>
            <button onClick={() => navigate("/app")} data-testid="receipt-home" className="col-span-2 flex items-center justify-center gap-2 border border-slate-200 bg-white hover:bg-slate-50 text-slate-700 font-semibold py-3 rounded-xl"><Home className="h-4 w-4" />Kembali ke Beranda</button>
          </div>
        )}
      </div>
    </div>
  );
}
